import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
    MessageSquarePlus,
    MoreHorizontal,
    Pencil,
    Trash2,
    PanelLeftClose,
} from "lucide-react";
import { useAuth, authHeaders } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function relativeTime(iso) {
    if (!iso) return "";
    try {
        const then = new Date(iso).getTime();
        const diffSec = Math.floor((Date.now() - then) / 1000);
        if (diffSec < 60) return "just now";
        if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
        if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
        if (diffSec < 7 * 86400) return `${Math.floor(diffSec / 86400)}d ago`;
        return new Date(iso).toLocaleDateString();
    } catch {
        return "";
    }
}

export default function ChatsSidebar({
    open,
    onToggle,
    activeSessionId,
    onSelectSession,
    onNewChat,
    refreshKey,
}) {
    const { user, token } = useAuth();
    const [sessions, setSessions] = useState([]);
    const [openMenuId, setOpenMenuId] = useState(null);

    const load = useCallback(async () => {
        if (!token) return;
        try {
            const res = await axios.get(`${API}/sessions`, {
                headers: authHeaders(token),
            });
            setSessions(res.data || []);
        } catch (e) {
            console.error("[sidebar] load failed", e);
        }
    }, [token]);

    useEffect(() => {
        load();
    }, [load, refreshKey]);

    if (!user || !open) return null;

    async function rename(sessionId) {
        const current = sessions.find((s) => s.session_id === sessionId);
        const next = window.prompt("Rename chat", current?.title || "");
        if (!next || !next.trim()) return;
        try {
            await axios.patch(
                `${API}/sessions/${sessionId}`,
                { title: next.trim() },
                { headers: authHeaders(token) },
            );
            setSessions((prev) =>
                prev.map((s) =>
                    s.session_id === sessionId ? { ...s, title: next.trim() } : s,
                ),
            );
        } catch (e) {
            console.error("[sidebar] rename failed", e);
        }
        setOpenMenuId(null);
    }

    async function remove(sessionId) {
        if (!window.confirm("Delete this chat?")) return;
        try {
            await axios.delete(`${API}/sessions/${sessionId}`, {
                headers: authHeaders(token),
            });
            setSessions((prev) => prev.filter((s) => s.session_id !== sessionId));
            if (activeSessionId === sessionId) onNewChat();
        } catch (e) {
            console.error("[sidebar] delete failed", e);
        }
        setOpenMenuId(null);
    }

    return (
        <aside
            className="flex w-[260px] shrink-0 flex-col border-r border-[#E5E5E5] bg-[#FAFAFA]"
            data-testid="chats-sidebar"
        >
            <div className="flex items-center justify-between border-b border-[#E5E5E5] px-3 py-3">
                <button
                    type="button"
                    onClick={onNewChat}
                    data-testid="new-chat-button"
                    className="inline-flex items-center gap-2 rounded-lg border border-[#E5E5E5] bg-white px-3 py-1.5 text-[13px] font-medium text-[#0A0A0A] transition hover:border-[#0A0A0A] hover:bg-white"
                >
                    <MessageSquarePlus size={14} strokeWidth={2} />
                    New chat
                </button>
                <button
                    type="button"
                    onClick={onToggle}
                    aria-label="Collapse sidebar"
                    data-testid="sidebar-collapse"
                    className="flex h-8 w-8 items-center justify-center rounded-md text-[#737373] transition hover:bg-[#F5F5F5] hover:text-[#0A0A0A]"
                >
                    <PanelLeftClose size={15} strokeWidth={1.75} />
                </button>
            </div>

            <div className="scroll-area flex-1 overflow-y-auto px-2 py-2">
                {sessions.length === 0 && (
                    <p className="px-3 py-6 text-center text-[12px] text-[#A3A3A3]">
                        No saved chats yet — start one below.
                    </p>
                )}
                {sessions.map((s) => {
                    const active = s.session_id === activeSessionId;
                    const menuOpen = openMenuId === s.session_id;
                    return (
                        <div
                            key={s.session_id}
                            className={`group relative mb-1 rounded-lg ${
                                active ? "bg-white shadow-[inset_0_0_0_1px_#E5E5E5]" : "hover:bg-white"
                            }`}
                            data-testid={`sidebar-session-${s.session_id}`}
                        >
                            <button
                                type="button"
                                onClick={() => onSelectSession(s.session_id)}
                                className="block w-full px-3 py-2 text-left"
                            >
                                <div className="line-clamp-1 text-[13px] font-medium text-[#0A0A0A]">
                                    {s.title || "Untitled chat"}
                                </div>
                                <div className="text-[11px] text-[#737373]">
                                    {relativeTime(s.last_message_at)} · {s.message_count} msgs
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenuId(menuOpen ? null : s.session_id);
                                }}
                                aria-label="Chat actions"
                                data-testid={`sidebar-menu-${s.session_id}`}
                                className="absolute right-1.5 top-1.5 hidden h-6 w-6 items-center justify-center rounded-md text-[#737373] transition hover:bg-[#F5F5F5] hover:text-[#0A0A0A] group-hover:flex"
                            >
                                <MoreHorizontal size={14} strokeWidth={1.75} />
                            </button>
                            {menuOpen && (
                                <div className="absolute right-1 top-8 z-10 w-36 overflow-hidden rounded-lg border border-[#E5E5E5] bg-white shadow-[0_4px_16px_rgba(0,0,0,0.06)]">
                                    <button
                                        type="button"
                                        onClick={() => rename(s.session_id)}
                                        data-testid={`sidebar-rename-${s.session_id}`}
                                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] text-[#0A0A0A] hover:bg-[#F5F5F5]"
                                    >
                                        <Pencil size={12} strokeWidth={2} /> Rename
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => remove(s.session_id)}
                                        data-testid={`sidebar-delete-${s.session_id}`}
                                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] text-[#DC2626] hover:bg-[#FEF2F2]"
                                    >
                                        <Trash2 size={12} strokeWidth={2} /> Delete
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </aside>
    );
}
